/**
 * How a flight's boot is going, as one value: what stage it has reached, which
 * phase it is inside, and what went wrong if anything did.
 *
 * The three travel together and always have — they are exactly the inputs of
 * `FlightStatusOverlay`, and nothing else reads any of them — but until B2 added
 * the third they were three signals declared side by side in each of the three
 * routes that mount a flight, and three props threaded through `FlightSurface`
 * to reach one component. Bundling them keeps a fourth from being added the same
 * way, and it is what lets `/vcr` and `/duel` show the same loading screen `/fly`
 * does for the cost of one line each.
 *
 * Deliberately not a store or a context: it is a plain record of accessors plus
 * one sink, so a route still owns its own instance and a test can hand
 * `FlightSurface` a fixed one without a reactive root.
 */

import { createSignal, type Accessor } from "solid-js";
import type { BootProgress } from "./boot-progress.ts";
import type { PaneStatus } from "./FlightStatusOverlay.tsx";

export interface BootStatus {
  readonly status: Accessor<PaneStatus>;
  /** Set only alongside an `"error"` status. */
  readonly errorMessage: Accessor<string | undefined>;
  /** The phase now running, or `undefined` before the scene's first report. */
  readonly progress: Accessor<BootProgress | undefined>;
  /** `FlightSceneCallbacks.onStatus`'s shape, so a route can pass this straight through. */
  readonly report: (status: PaneStatus, message?: string) => void;
  /** `FlightSceneCallbacks.onBootProgress`'s shape, likewise. */
  readonly reportProgress: (progress: BootProgress) => void;
}

/**
 * `initial` is what the route knows before it tries: `"initializing"` where
 * there is a WebGPU adapter to ask, `"unavailable"` where there is not (AD-005
 * — with no `navigator.gpu` the route never touches the renderer at all).
 */
export function createBootStatus(initial: PaneStatus): BootStatus {
  const [status, setStatus] = createSignal<PaneStatus>(initial);
  const [errorMessage, setErrorMessage] = createSignal<string | undefined>(undefined);
  const [progress, setProgress] = createSignal<BootProgress | undefined>(undefined);
  return {
    status,
    errorMessage,
    progress,
    report: (next, message) => {
      setStatus(next);
      setErrorMessage(message);
    },
    reportProgress: (next) => setProgress(next),
  };
}
